import { useQuery } from '@tanstack/react-query';
import type { AxiosError, AxiosInstance } from 'axios';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  useAxiosInstance,
} from 'shared/hooks/utils';
import z from 'zod/v4';

import { AutomationSchema } from './use-create-automation';

export const LoadAutomationSchema = AutomationSchema.and(
  z.object({
    id: z.uuid(),
    order: z.number(),
    lastActivityAt: z.string().nullable(),
  }),
);

export type Automation = z.infer<typeof LoadAutomationSchema>;

export async function loadAutomationQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(`/automations/${queryKey[1]}`);

  const safeParse = LoadAutomationSchema.safeParse(data);

  if (!safeParse.success) {
    handleZodError(safeParse, 'automation');
    throw safeParse.error;
  }

  return safeParse.data;
}

type LoadAutomationVariables = {
  automationId: string | null;
  enabled?: boolean;
};

const emptyAutomation: Automation = {
  id: '',
  name: '',
  active: false,
  description: null,
  targetModel: 'invoice',
  triggers: [],
  conditions: [],
  actions: [],
  order: 0,
  lastActivityAt: null,
};

export function useLoadAutomation({ automationId, enabled = true }: LoadAutomationVariables) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<Automation, ApiError>({
    queryKey: ['automation', automationId],
    queryFn: context => loadAutomationQueryFn(axiosInstance, context),
    enabled,
    staleTime: 1,
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;

      return emptyAutomation;
    },
  });

  return addResourceNameToQueryResult<Automation, AxiosError, 'automation', 'PreventUndefined'>(
    'automation',
    queryResult,
    emptyAutomation,
  );
}
